import * as React from 'react';
import { memo } from 'react';

import Icon from 'components/atoms/Icon';
import Image, { ImageProps } from 'components/atoms/Image';
import SVGCardTrackingIcon from 'components/molecules/Card/svg/card-tracking-icon';

import { isNotEmptyObject } from '@impuls-coach/util-is';
import i18n from 'helper/i18n';
import { toDashed } from 'helper/string';

import { RECIPE_MASTER_CHANNELS } from 'store/programItems';

export type CardPreviewProps = {
	categoryIcon?: string;
	type?: string;
	logo?: ImageProps;
	thirdPartyLogo?: ImageProps;
	masterChannel?: string;
	previewImage?: ImageProps;
	fallbackImage?: ImageProps;
	title?: string;
	teaserText?: string;
};

const getType = (itemType: string): string => {
	switch (toDashed(itemType)) {
		case 'product-editorial':
			return 'recommendation';
		case 'product-dynamic':
			return 'productDynamic';
		case 'todo':
			return 'task';
		default:
			return toDashed(itemType);
	}
};

class CardPreview extends React.Component<CardPreviewProps> {
	public static displayName = 'CardPreview';
	public static defaultProps: CardPreviewProps = {};

	public renderTopicIcon(categoryIcon: string) {
		return (
			<div className="m-card__topic-icon">
				<Icon iconName={categoryIcon} />
			</div>
		);
	}

	public renderFigureIcon(categoryIcon: string) {
		switch (categoryIcon) {
			case 'recommendation':
				categoryIcon = 'cart';
				break;
			case 'slogan':
				categoryIcon = 'quote';
				break;
			default:
				break;
		}

		return (
			<div className="m-card__figure-icon">
				<Icon iconName={categoryIcon} />
			</div>
		);
	}

	public renderCategoryTitle(type: string) {
		return (
			<p className="m-card__topic-text">
				{type && i18n(`react.impulsCoach.components.card.${type}.categoryTitle`)}
			</p>
		);
	}

	public renderPreviewImage(type: string, previewImage?: ImageProps): any {
		const cardTypesWithoutImages: string[] = ['tracking'];

		if (previewImage && cardTypesWithoutImages.indexOf(type) === -1) {
			return <Image {...previewImage} />;
		}
	}

	public renderThirdPartyLogo(thirdPartyLogo?: ImageProps) {
		if (!isNotEmptyObject(thirdPartyLogo)) {
			return null;
		}

		const imageProps: ImageProps = { ...thirdPartyLogo, ...{ modifier: 'fit-parent' } } as any;

		return (
			<div className="m-card__logo-third-party">
				<Image {...imageProps} />
			</div>
		);
	}

	public render() {
		const {
			title,
			type,
			categoryIcon,
			masterChannel,
			logo,
			previewImage,
			fallbackImage,
			thirdPartyLogo,
		} = this.props;

		return (
			<article className={`m-card${type ? ` m-card--${getType(type)}` : ''} state-m-card--preview`}>
				<div className="m-card__topic">
					{this.renderCategoryTitle(getType(type || ''))}
					{this.renderTopicIcon(categoryIcon || '')}
				</div>
				{isNotEmptyObject(logo) && masterChannel && RECIPE_MASTER_CHANNELS.includes(masterChannel) && (
					<div className="m-card__logo">
						<Image {...logo!} />
					</div>
				)}
				<div className="m-card__wrapper">
					<div className="m-card__head">
						<div className="m-card__image">
							{this.renderPreviewImage(type || '', previewImage || fallbackImage)}
						</div>
						{this.renderThirdPartyLogo(thirdPartyLogo)}
						<div className="m-card__figure">
							<svg
								className="m-card__figure-line"
								xmlns="http://www.w3.org/2000/svg"
								role="presentation"
								viewBox="904 -507 320 115"
								preserveAspectRatio="none"
							>
								<g
									stroke="none"
									strokeWidth="1"
									fill="none"
									fillRule="evenodd"
									transform="translate(904.000000, -507.000000)"
								>
									<polygon
										className="m-card__polygon--left"
										points="0 36.0150376 320 115.600741 320 115 0 115"
									/>
									<polygon
										className="m-card__polygon--right"
										points="0 102.459265 320 0.697443618 320 115 0 115"
									/>
									<polygon
										className="m-card__polygon--top"
										points="0 101.541627 117.265896 65 320 114.788912 320 115 0 115"
									/>
								</g>
							</svg>
							{type === 'tracking' && <SVGCardTrackingIcon />}
							{this.renderFigureIcon(getType(type || ''))}
						</div>
					</div>
					<div className="m-card__content">
						<div className="m-card__info">{title && <h4 className="m-card__info-title">{title}</h4>}</div>
					</div>
				</div>
			</article>
		);
	}
}

export { CardPreview };
export default memo(CardPreview);
